import { formatMoney } from 'accounting'

import { faCheck, faEdit, faShare } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { Button } from '@domain-group/suit-theme'

import useCampaign from '@hooks/useCampaign'
import type { CampaignQuotePayload } from '@hooks/useCampaignQuote'
import useSelector from '@hooks/useSelector'

import { confirmAction } from '@functions/entity'
import { findStatusByKey } from '@functions/status'
import type { ConfirmActionParams } from '@functions/entity'

interface ApproveQuoteButtonProps {
  campaignQuotePayload: CampaignQuotePayload,
}

type HandleApproveButtonClickParams = {
  approveQuoteConfirmActionPayload: ConfirmActionParams,
  assignRequiredDates: (payload: { isApprovalFlow: boolean }) => void,
  editCampaignDates: (params: { callbacks: { successFn: (startDate?: string) => void } }) => void,
  processFn: (params: { startDate?: string }) => void,
  recalculateEditions: boolean,
  requireQuoteItemRequiredByDate: boolean,
}

const handleApproveButtonClick = (params: HandleApproveButtonClickParams) => {
  const {
    approveQuoteConfirmActionPayload,
    assignRequiredDates,
    editCampaignDates,
    processFn,
    recalculateEditions,
    requireQuoteItemRequiredByDate,
  } = params

  if (requireQuoteItemRequiredByDate){
    return assignRequiredDates({ isApprovalFlow: true })
  }

  if (recalculateEditions){
    return editCampaignDates({ callbacks: { successFn: (startDate?: string) => processFn({ startDate }) } })
  }

  return confirmAction(approveQuoteConfirmActionPayload)
}

const ApproveQuoteButton = (props: ApproveQuoteButtonProps) => {
  const {
    campaignQuotePayload: {
      callbacks: {
        addContribution,
        assignRequiredDates,
        processQuoteApproval: processFn,
        sendQuoteForApproval,
        updateCampaignQuote: updateFn,
      },
      campaign,
      campaignQuote: {
        authorized,
      },
      contributionsDifference,
      contributionsSufficient,
      isApproved,
      isAwaitingReview,
      recalculateEditions,
      requiresAuthorization,
      requiresContributions,
      updating,
      userCanApproveQuotes,
    },
  } = props

  const { statuses } = useSelector(reduxState => reduxState.entities)
  const pendingStatus = findStatusByKey(statuses, 'pending')

  const { agency, callbacks: { editCampaignDates } } = useCampaign(campaign)

  // @ts-expect-error TS2339
  const requireQuoteItemRequiredByDate = agency?.options?.require_quote_item_required_by_date

  const approveQuoteConfirmActionPayload = {
    callbacks: {
      actionFn: processFn,
    },
    options: {
      shouldRedirect: true,
    },
    strings: {
      actionString: 'Approve',
      confirmString: 'Are you sure? This will add all the items to your campaign.',
      entityName: 'quote',
    },
  }

  const adjustQuoteConfirmActionPayload = {
    callbacks: {
      actionFn: updateFn,
    },
    options: {
      icon: 'warning',
      entityParams: {
        // @ts-expect-error TS2339
        status_id: pendingStatus?.id,
      },
    },
    strings: {
      actionString: 'Adjust',
      confirmString: 'Are you sure? This will set the quotes status back to pending.',
      entityName: 'quote',
      toastText: 'Quote Adjusted',
    },
  }

  // Approved
  if (isApproved){
    return (
      <Button
        buttonStyle="ghostCreate"
        // @ts-expect-error TS2322
        marginLeft="medium"
        size="medium"
        width="auto"
      >
        Approved
      </Button>
    )
  }

  // Contributions don't match quote totals
  if (requiresContributions && !contributionsSufficient){
    return (
      <Button
        buttonStyle="ghostDestroy"
        // @ts-expect-error TS2322
        marginLeft="medium"
        // @ts-expect-error TS2554
        onClick={() => addContribution(contributionsDifference)}
        size="medium"
        width="auto"
      >
        {`${formatMoney(contributionsDifference)} Required`}
      </Button>
    )
  }

  // Can Approve & Quote Authorized
  if (userCanApproveQuotes && (!requiresAuthorization || authorized)){
    return (
      <Button
        buttonStyle="primaryCreate"
        icon={<FontAwesomeIcon icon={faCheck} />}
        loading={updating}
        // @ts-expect-error TS2322
        marginLeft="medium"
        onClick={() => handleApproveButtonClick({
          assignRequiredDates,
          editCampaignDates,
          requireQuoteItemRequiredByDate,
          recalculateEditions,
          approveQuoteConfirmActionPayload,
          processFn,
        })}
        size="medium"
        width="auto"
      >
        Approve
      </Button>
    )
  }

  // Can't Approve & Authorized
  if (!userCanApproveQuotes && (!requiresAuthorization || authorized)){
    return isAwaitingReview ? (
      <Button
        buttonStyle="ghostEdit"
        icon={<FontAwesomeIcon icon={faEdit} />}
        // @ts-expect-error TS2322
        marginLeft="medium"
        // @ts-expect-error TS2345
        onClick={() => confirmAction(adjustQuoteConfirmActionPayload)}
        size="medium"
        width="auto"
      >
        Adjust Quote
      </Button>
    ) : (
      <Button
        buttonStyle="ghostEdit"
        icon={<FontAwesomeIcon icon={faShare} />}
        // @ts-expect-error TS2322
        marginLeft="medium"
        onClick={() => sendQuoteForApproval()}
        size="medium"
        width="auto"
      >
        Send For Approval
      </Button>
    )
  }

  return null
}

export default ApproveQuoteButton
